import React from 'react';
import { FiInfo, FiLayers, FiUsers, FiDatabase, FiTool, FiShare2, FiArrowRight } from 'react-icons/fi';
import '../styles/about.css';
import SEO from '../components/SEO';

const AboutPage = () => {
    return (
        <div className="about-bento-container py-5">
            <SEO
                title="About"
                description="Learn about the Electrospinning Community Data Collection Project, its purpose, features, and origins."
            />
            {/* Header Section */}
            <header className="text-center mb-5 bento-header">
                <h1 className="display-4 font-weight-bold">About the Platform</h1>
                <p className="lead text-muted about-intro-bento">
                    A platform for collecting, organizing, and sharing experimental electrospinning datasets.
                </p>
            </header>

            {/* Bento Grid Layout */}
            <div className="bento-grid-about">

                {/* Tile 1: Project Origin & Purpose (Wide, Top) */}
                <div className="bento-tile tile-origin">
                    <div className="tile-icon-bg"><FiInfo /></div>
                    <div className="tile-content">
                        <span className="tile-label mb-2 d-block">Origin & Purpose</span>
                        <h2 className="tile-title-main">Project Origin</h2>
                        <p className="tile-desc mt-2">
                            The platform was developed as part of the <strong>PSG897</strong> research project at
                            <strong> Tallinn University of Technology (TalTech)</strong>. The initiative focuses
                            on creating structured electrospinning datasets to provide open data for scientific research.
                        </p>
                    </div>
                </div>

                {/* Tile 2: Why Electrospinning? (Square) */}
                <div className="bento-tile tile-why">
                    <div className="tile-icon-bg"><FiLayers /></div>
                    <div className="tile-content">
                        <span className="tile-label mb-2 d-block">Motivation</span>
                        <h3 className="tile-title-sm">The Challenge</h3>
                        <p className="tile-desc-sm mt-2">
                            Electrospinning involves many experimental parameters that are often inconsistently reported
                            in literature. This leads to fragmented datasets and difficulties in performing reliable
                            cross-study analysis and data-driven material discovery.
                        </p>
                    </div>
                </div>

                {/* Tile 3: Who Can Benefit? (Square) */}
                <div className="bento-tile tile-who">
                    <div className="tile-icon-bg"><FiUsers /></div>
                    <div className="tile-content text-white">
                        <span className="tile-label text-white-50 mb-2 d-block">Audience</span>
                        <h3 className="tile-title-sm text-white">Who Uses the Platform</h3>
                        <p className="tile-desc-sm text-white-50 mt-2">
                            The platform is designed for Researchers specializing in materials science, Data Scientists
                            developing predictive models, and Students exploring experimental nanofibers research.
                        </p>
                    </div>
                </div>

                {/* Tile 4: Platform Features Sub-Grid (Tall/Wide) */}
                <div className="bento-tile tile-features">
                    <div className="tile-content w-100">
                        <span className="tile-label mb-3 d-block text-center text-md-start">Core Capabilities</span>
                        <div className="features-inner-grid">

                            <div className="feature-block">
                                <div className="feature-icon"><FiDatabase /></div>
                                <div>
                                    <h4 className="feature-heading">Dataset Repository</h4>
                                    <p className="feature-text">A curated collection of electrospinning experiments extracted from published studies.</p>
                                </div>
                            </div>

                            <div className="feature-block">
                                <div className="feature-icon"><FiTool /></div>
                                <div>
                                    <h4 className="feature-heading">Data Submission</h4>
                                    <p className="feature-text">Structured forms allow researchers to contribute experimental data in a consistent format.</p>
                                </div>
                            </div>

                            <div className="feature-block">
                                <div className="feature-icon"><FiShare2 /></div>
                                <div>
                                    <h4 className="feature-heading">Data-Driven Research</h4>
                                    <p className="feature-text">Structured datasets enable statistical analysis and machine learning for electrospinning research.</p>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* Tile 5: Get Involved / CTA (Wide, Bottom) */}
                <div className="bento-tile tile-cta text-center align-items-center justify-content-center">
                    <div className="tile-content align-items-center justify-content-center">
                        <h2 className="tile-title text-white mb-3">Contribute to the Dataset</h2>
                        <p className="tile-desc text-white-50 mb-4" style={{ maxWidth: '600px' }}>
                            Researchers are invited to contribute electrospinning datasets from published studies or ongoing research to help build a shared resource for the community.
                        </p>
                        <a href="/dataSubmission" className="bento-btn bento-btn-accent">
                            Submit Data <FiArrowRight className="ms-2" />
                        </a>
                    </div>
                </div>

            </div>

            {/* Credibility Sections */}
            <div className="row mt-5 pt-4">
                <div className="col-md-4 mb-4">
                    <div className="p-4 bg-light rounded shadow-sm h-100">
                        <h5 className="font-weight-bold mb-3" style={{ color: '#003366' }}>Project Citation</h5>
                        <p className="small text-muted mb-0">
                            Mahdian, M., et al. (2024). Electrospinning-data.org: A Platform for Open Science.
                            <em> TechRxiv.</em> DOI: <a href="https://doi.org/10.36227/techrxiv.173268413.56840742/v1" target="_blank" rel="noopener noreferrer">10.36227/techrxiv.173268413.56840742/v1</a>
                        </p>
                    </div>
                </div>
                <div className="col-md-4 mb-4">
                    <div className="p-4 bg-light rounded shadow-sm h-100">
                        <h5 className="font-weight-bold mb-3" style={{ color: '#003366' }}>Institutional Affiliation</h5>
                        <ul className="small text-muted mb-0 ps-3">
                            <li>Tallinn University of Technology (TalTech)</li>
                        </ul>
                    </div>
                </div>
                <div className="col-md-4 mb-4">
                    <div className="p-4 bg-light rounded shadow-sm h-100">
                        <h5 className="font-weight-bold mb-3" style={{ color: '#003366' }}>License Information</h5>
                        <p className="small text-muted mb-0">
                            Datasets on this platform are shared under a
                            <strong> Creative Commons Attribution 4.0 International (CC BY 4.0)</strong> license unless otherwise specified.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AboutPage;
